import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { logout, startLogin, takeReturnPath, trySilentAuth } from '@/lib/auth'
import type { AuthUser } from '@/lib/types'
import { AuthContext, type AuthState } from '@/hooks/use-auth'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false

    async function init() {
      // 1. An existing cookie session.
      let current = await api.currentUser()

      // 2. Otherwise a silent SSO round-trip, in case the user is signed in elsewhere.
      if (!current) {
        try {
          const result = await trySilentAuth()
          if (await api.exchangeCode(result)) current = await api.currentUser()
        } catch {
          // Not signed in at the SSO – browsing as a guest is fine.
        }
      }

      if (cancelled) return
      setUser(current)
      setIsLoading(false)

      if (current) {
        // Lists fetched as a guest are filtered differently.
        void queryClient.invalidateQueries()
        const back = takeReturnPath()
        if (back && back !== window.location.pathname + window.location.search) {
          navigate(back, { replace: true })
        }
      }
    }

    void init()
    return () => {
      cancelled = true
    }
  }, [queryClient, navigate])

  const value = useMemo<AuthState>(
    () => ({
      user,
      isLoggedIn: !!user,
      isLoading,
      isModerator: !!user && (user.isAdmin || user.isMod),
      isAdmin: !!user?.isAdmin,
      login: startLogin,
      logout,
    }),
    [user, isLoading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
